import { SerializedStyles } from '@emotion/react';
import { useAtom } from 'jotai';
import * as React from 'react';

import { FoldButton } from '~src/category/fold-button';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { AriaGrid } from '~src/ui/aria-grid';
import { ListState } from '~src/ui/list-state';

import { foldAtom } from './state/category-extesion-atoms';

/**
 * TODO: add animations for the chevron and opening/closing the list
 */

/**
 * For now the fold count is static
 */
const FOLD_COUNT = {
  min: 2,
  medium: 10,
  max: Infinity,
} as const;

interface InlineExtensionListProps<T> {
  listState: ListState<T>;
  listStyles?: SerializedStyles;
  getId: (item: T) => string;
  children: ({ item, hidden }: { item: T; hidden: boolean }) => React.ReactNode;
}

export const InlineExtensionList = <T,>({
  listState,
  listStyles,
  getId,
  children,
}: InlineExtensionListProps<T>) => {
  const { tICU } = useTranslations();
  const [fold, setFold] = useAtom(foldAtom);

  const enabled = listState.items.length > FOLD_COUNT['min'];

  const selectedIndex = listState.items.findIndex(
    item => getId(item) === getId(listState.getSelectedItem()!)
  );
  const adjustedFoldCount =
    selectedIndex >= FOLD_COUNT[fold] ? FOLD_COUNT[fold] - 1 : FOLD_COUNT[fold];

  React.useEffect(() => {
    setFold('min');
  }, [listState.items.length]);

  const handleFold = () =>
    setFold(prev => {
      if (prev === 'min') {
        if (listState.items.length > FOLD_COUNT['medium']) {
          return 'medium';
        } else {
          return 'max';
        }
      } else if (prev === 'medium') {
        return 'max';
      } else {
        return 'min';
      }
    });

  const folded = ['min', 'medium'].includes(fold);

  return (
    <div css={listStyles}>
      <AriaGrid<T> listState={listState}>
        {(item, index) =>
          children({
            item,
            hidden: folded && index !== selectedIndex && index >= adjustedFoldCount,
          })
        }
      </AriaGrid>
      {enabled && (
        <FoldButton
          folded={folded}
          foldedText={tICU('widget.shipping_categories_folding.BUTTON.SHOW_MORE')}
          unfoldedText={tICU('widget.shipping_categories_folding.BUTTON.SHOW_LESS')}
          ariaSupplementaryText={tICU('widget.aria.SHOW_MORE_ITEMS', {
            count:
              listState.items.length > FOLD_COUNT['medium'] && fold === 'min'
                ? FOLD_COUNT['medium'] - FOLD_COUNT['min']
                : listState.items.length - FOLD_COUNT[fold],
          })}
          style={{
            borderTop: '1px solid #E8E8E8',
            paddingTop: '12px',
            paddingBottom: '20px',
          }}
          textStyle={{ fontVariationSettings: "'wght' 500" }}
          onClick={handleFold} // TODO ask product if these should be logged in posthog
        />
      )}
    </div>
  );
};
